import { RelationType } from '../types';

const MODAL_ENDPOINT = process.env.REACT_APP_MODAL_ENDPOINT;

interface WordCheckResult {
  relation: RelationType;
  justification: string;
}

const cache = new Map<string, WordCheckResult>();

export const clearCache = () => {
  cache.clear();
};

export const checkWordRelation = async (
  word1: string, 
  word2: string, 
  expectedRelation?: RelationType
): Promise<WordCheckResult> => {
  const cacheKey = `${word1}-${word2}`;
  if (cache.has(cacheKey)) {
    return cache.get(cacheKey) as WordCheckResult;
  }

  if (!MODAL_ENDPOINT) {
    console.error('REACT_APP_MODAL_ENDPOINT not configured');
    return {
      relation: 'invalid',
      justification: 'Modal endpoint not configured'
    };
  }

  try {
    // Call Modal endpoint
    const response = await fetch(MODAL_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        word_pairs: [{ word1, word2 }]
      })
    });

    if (!response.ok) {
      // Try to get error details from response
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage = errorData.detail || errorData.message || JSON.stringify(errorData);
        console.error('Modal API Error:', errorData);
      } catch (e) {
        const errorText = await response.text();
        errorMessage = errorText || errorMessage;
        console.error('Modal API Error (text):', errorText);
      }
      throw new Error(errorMessage);
    }

    const data = await response.json();
    const results = data.results;
    
    if (!results || results.length === 0) {
      throw new Error('No results in response');
    }

    const result = results[0];
    
    console.log(`Word Pair Check: "${word1}" - "${word2}"`);
    console.log('Result:', JSON.stringify(result, null, 2));

    // Cache the result in both directions
    cache.set(cacheKey, result);
    cache.set(`${word2}-${word1}`, result);

    // Log the result with appropriate color
    if (result.relation === 'invalid') {
      console.log('\x1b[31m%s\x1b[0m', `✗ INVALID`);  // Red
    } else {
      console.log(
        '\x1b[32m%s\x1b[0m',
        `✓ ${result.relation.toUpperCase()}`
      );  // Green
    }

    return result;
  } catch (error) {
    console.error('Error checking word relation:', error);
    return {
      relation: 'invalid',
      justification: 'Error checking word relation'
    };
  }
};

export interface WordPair {
  word1: string;
  word2: string;
}

export interface BatchWordCheckResult {
  results: WordCheckResult[];
}

/**
 * Batch check multiple word relations in a single API call.
 * Checks cache first and only makes API calls for uncached pairs.
 */
export const checkWordRelationsBatch = async (
  pairs: WordPair[]
): Promise<WordCheckResult[]> => {
  if (pairs.length === 0) return [];

  // Check cache for all pairs first
  const results: WordCheckResult[] = [];
  const uncachedPairs: WordPair[] = [];
  const uncachedIndices: number[] = [];

  pairs.forEach((pair, index) => {
    const cacheKey = `${pair.word1}-${pair.word2}`;
    const reverseCacheKey = `${pair.word2}-${pair.word1}`;
    
    if (cache.has(cacheKey)) {
      results[index] = cache.get(cacheKey) as WordCheckResult;
    } else if (cache.has(reverseCacheKey)) {
      results[index] = cache.get(reverseCacheKey) as WordCheckResult;
    } else {
      uncachedPairs.push(pair);
      uncachedIndices.push(index);
    }
  });

  // If all pairs are cached, return immediately
  if (uncachedPairs.length === 0) {
    return results;
  }

  // Make single API call for all uncached pairs
  if (!MODAL_ENDPOINT) {
    console.error('REACT_APP_MODAL_ENDPOINT not configured');
    uncachedIndices.forEach(index => {
      results[index] = {
        relation: 'invalid',
        justification: 'Modal endpoint not configured'
      };
    });
    return results;
  }

  try {
    const response = await fetch(MODAL_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        word_pairs: uncachedPairs
      })
    });

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage = errorData.detail || errorData.message || JSON.stringify(errorData);
        console.error('Modal API Error:', errorData);
      } catch (e) {
        const errorText = await response.text();
        errorMessage = errorText || errorMessage;
        console.error('Modal API Error (text):', errorText);
      }
      throw new Error(errorMessage);
    }

    const data = await response.json();
    const apiResults = data.results;
    
    if (!apiResults || apiResults.length !== uncachedPairs.length) {
      throw new Error(`Expected ${uncachedPairs.length} results, got ${apiResults?.length || 0}`);
    }

    // Process results and update cache
    apiResults.forEach((result: WordCheckResult, i: number) => {
      const pair = uncachedPairs[i];
      const cacheKey = `${pair.word1}-${pair.word2}`;
      const reverseCacheKey = `${pair.word2}-${pair.word1}`;
      
      // Store result at correct index
      const resultIndex = uncachedIndices[i];
      results[resultIndex] = result;
      
      // Cache in both directions
      cache.set(cacheKey, result);
      cache.set(reverseCacheKey, result);
      
      // Log the result
      if (result.relation === 'invalid') {
        console.log(`✗ INVALID: "${pair.word1}" - "${pair.word2}"`);
      } else {
        console.log(`✓ ${result.relation.toUpperCase()}: "${pair.word1}" - "${pair.word2}"`);
      }
    });

    return results;
  } catch (error) {
    console.error('Error checking word relations batch:', error);
    // Fill in error results for uncached pairs
    uncachedIndices.forEach(index => {
      results[index] = {
        relation: 'invalid',
        justification: 'Error checking word relation'
      };
    });
    return results;
  }
};

export const isValidChain = (relations: RelationType[]): boolean => {
  if (relations.length < 2) return false;
  
  for (let i = 1; i < relations.length; i++) {
    if (relations[i] === relations[i - 1] || 
        relations[i] === 'invalid' || 
        relations[i - 1] === 'invalid') {
      return false;
    }
  }
  
  return true;
};